/* ============================================================
   ck-pdp-gallery
   5s auto-rotate with pure opacity crossfade.
   - Manages css-slide-active class directly (doesn't rely on theme)
   - Pauses on hover, touch, manual thumb click, or hidden tab
   - Respects prefers-reduced-motion
   ============================================================ */
(function () {
  'use strict';

  if (window.__ckPdpGalleryLoaded) return;
  window.__ckPdpGalleryLoaded = true;

  const INTERVAL_MS = 5000;
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    const gallery = document.querySelector('.product-gallery');
    if (!gallery) return;
    if (gallery.hasAttribute('data-ck-autorotate')) return; /* already initialized */
    /* Mobile uses native swipe + snap — skip crossfade + autorotate */
    if (window.matchMedia('(max-width: 767px)').matches) return;

    const thumbs = Array.from(gallery.querySelectorAll('.product-gallery__thumbnails-holder .thumbnail'));
    const slides = Array.from(gallery.querySelectorAll('.css-slide'));
    if (slides.length < 2) return;

    gallery.setAttribute('data-ck-autorotate', '');
    window.__ckPdpGalleryInit = true;

    /* Stop the underlying css-slider's IntersectionObserver from racing autorotate.
       With opacity-crossfade all slides occupy the same viewport position, so the
       observer reads every slide as 100% visible and on each lazy-image load
       reassigns css-slide-active to whichever entry is processed last.
       The css-slider may not have initialised yet at this moment, so kill the
       observer now AND re-kill on its 'ready' event AND poll for a few seconds. */
    const innerSlider = gallery.querySelector('css-slider');
    function killInnerObserver() {
      if (innerSlider && innerSlider.OBSERVER) {
        innerSlider.OBSERVER.disconnect();
        innerSlider.OBSERVER = null;
      }
    }
    killInnerObserver();
    if (innerSlider) {
      innerSlider.addEventListener('ready', killInnerObserver);
      let killTries = 30;
      const killPoll = setInterval(() => {
        killInnerObserver();
        if (--killTries <= 0) clearInterval(killPoll);
      }, 100);
    }

    let timer = null;
    let paused = false;
    let firstSlideHintConsumed = false;

    gallery.setAttribute('data-ck-first-slide', '');
    const chevronSvg = (dir) =>
      `<svg viewBox="0 0 36 120" width="36" height="120" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true" preserveAspectRatio="xMidYMid meet">` +
      `<path d="${dir === 'prev' ? 'M30 6 L6 60 L30 114' : 'M6 6 L30 60 L6 114'}"/></svg>`;
    gallery.insertAdjacentHTML(
      'beforeend',
      `<button type="button" class="ck-gallery-chevron ck-gallery-chevron--prev" aria-label="Previous image">${chevronSvg('prev')}</button>` +
      `<button type="button" class="ck-gallery-chevron ck-gallery-chevron--next" aria-label="Next image">${chevronSvg('next')}</button>`
    );
    const chevPrev = gallery.querySelector('.ck-gallery-chevron--prev');
    const chevNext = gallery.querySelector('.ck-gallery-chevron--next');

    function consumeFirstSlideHint() {
      if (firstSlideHintConsumed) return;
      firstSlideHintConsumed = true;
      gallery.removeAttribute('data-ck-first-slide');
    }

    function activeIndex() {
      const i = slides.findIndex((s) => s.classList.contains('css-slide-active'));
      return i >= 0 ? i : 0;
    }

    function setActive(nextIdx) {
      slides.forEach((s, i) => s.classList.toggle('css-slide-active', i === nextIdx));
      thumbs.forEach((t, i) => {
        t.classList.toggle('active', i === nextIdx);
        if (i === nextIdx) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    function tick() {
      if (paused || document.hidden) return;
      consumeFirstSlideHint();
      const next = (activeIndex() + 1) % slides.length;
      setActive(next);
    }

    chevPrev.addEventListener('click', function (e) {
      if (!e.isTrusted) return;
      e.preventDefault();
      e.stopPropagation();
      stop();
      paused = true;
      consumeFirstSlideHint();
      const next = (activeIndex() - 1 + slides.length) % slides.length;
      setActive(next);
    });
    chevNext.addEventListener('click', function (e) {
      if (!e.isTrusted) return;
      e.preventDefault();
      e.stopPropagation();
      stop();
      paused = true;
      consumeFirstSlideHint();
      const next = (activeIndex() + 1) % slides.length;
      setActive(next);
    });

    function start() {
      if (reducedMotion) return;
      stop();
      timer = setInterval(tick, INTERVAL_MS);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    /* Pause on hover */
    gallery.addEventListener('mouseenter', () => { paused = true; });
    gallery.addEventListener('mouseleave', () => { paused = false; });
    gallery.addEventListener('touchstart', () => { paused = true; }, { passive: true });

    /* Manual thumb click: stop auto-rotate and take over */
    thumbs.forEach((t, i) => {
      t.addEventListener(
        'click',
        function (e) {
          if (e.isTrusted) {
            stop();
            paused = true;
            consumeFirstSlideHint();
            setActive(i);
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });

    /* Pause when tab hidden */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else if (!paused) start();
    });

    /* Always boot from slide 0 — by the time init runs, the inner observer may
       have already advanced past 0 from lazy-image loads firing the threshold. */
    setActive(0);

    /* Lockdown: for as long as the first-slide hint is still present, revert
       any external writes back to slide 0. Catches the inner css-slider's
       'ready' / variant-change handlers re-asserting their own index after we
       disconnect its observer. Self-triggered toggles are filtered via a
       reentrance guard. */
    let reverting = false;
    const lockdownObs = new MutationObserver(() => {
      if (firstSlideHintConsumed || reverting) return;
      const idx = slides.findIndex((s) => s.classList.contains('css-slide-active'));
      if (idx !== 0) {
        reverting = true;
        setActive(0);
        Promise.resolve().then(() => { reverting = false; });
      }
    });
    slides.forEach((s) => lockdownObs.observe(s, { attributes: true, attributeFilter: ['class'] }));

    start();
  }

  function tryInit(retries) {
    const gallery = document.querySelector('.product-gallery');
    const slides = gallery ? gallery.querySelectorAll('.css-slide') : [];
    if (gallery && slides.length >= 2) {
      init();
      return;
    }
    if (retries > 0) {
      setTimeout(() => tryInit(retries - 1), 200);
    }
  }

  function boot() {
    tryInit(20); /* up to ~4s */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
  }
})();
